/**
 * Token estimation utility for dynamic model scaling
 * Estimates tokens to decide between gpt-4o-mini and gpt-4o
 */

/**
 * Estimates the number of tokens in a text string
 * Uses multiple methods for better accuracy
 */
export const estimateTokens = (text: string): number => {
  if (!text) return 0;
  
  // Method 1: Character count ÷ 4 (GPT rough estimate)
  const charBasedEstimate = Math.ceil(text.length / 4);
  
  // Method 2: Word count × 1.3 (more accurate for English)
  const words = text.trim().split(/\s+/).filter(Boolean);
  const wordBasedEstimate = Math.ceil(words.length * 1.3);
  
  // Method 3: Special handling for structured content
  let structuredBonus = 0;
  
  // Biography data adds complexity
  if (text.includes('Biography:') || text.includes('Personal details:')) {
    structuredBonus += 50;
  }
  
  // Complex prompts with multiple instructions
  if (text.includes('Create a') && text.includes('meditation') && text.includes('personalized')) {
    structuredBonus += 30;
  }
  
  // TAONG-AF explicit content has nuanced language requirements
  if (text.includes('TAONG-AF') || text.includes('explicit meditation')) {
    structuredBonus += 100;
  }
  
  // Use the higher of the two estimates plus structured complexity
  const finalEstimate = Math.max(charBasedEstimate, wordBasedEstimate) + structuredBonus;
  
  console.log('Token estimation:', {
    text: text.substring(0, 100) + '...',
    textLength: text.length,
    charBased: charBasedEstimate,
    wordBased: wordBasedEstimate,
    structuredBonus,
    finalEstimate
  });
  
  return finalEstimate;
};

/**
 * Determines if advanced model (gpt-4o) should be used
 * Based on token count and latency tolerance
 */
export const shouldUseAdvancedModel = (
  tokens: number, 
  latencyToleranceSeconds: number,
  sessionType?: string,
  categoryId?: string
): boolean => {
  console.log('Model selection criteria:', {
    tokens,
    latencyToleranceSeconds,
    sessionType,
    categoryId
  });
  
  // Always use mini for template-generated content (bypasses OpenAI anyway)
  if (sessionType === 'template') {
    return false;
  }
  
  // Sample sessions prioritize speed
  if (sessionType === 'sample') {
    return false;
  }
  
  // Main decision matrix: tokens > 2500 AND latency tolerance > 5s
  const meetsThresholds = tokens > 2500 && latencyToleranceSeconds > 5;
  
  // Core categories use standard processing
  const isCoreCategory = [
    'meditation', 'anxiety', 'massage', 'motivation', 
    'memory', 'sleep', 'be_still', 'self_love'
  ].includes(categoryId || '');
  
  // ZEN-AF and News Meditation always use advanced model for nuanced handling
  const isAdvancedCategory = categoryId === 'zen-af' || 
                            categoryId === 'news-meditation' ||
                            sessionType === 'explicit';
  
  // Use advanced model if:
  // 1. Meets basic thresholds, OR
  // 2. Advanced category content (requires nuanced language handling)
  const shouldUseAdvanced = meetsThresholds || 
    (isAdvancedCategory && latencyToleranceSeconds > 3);
  
  console.log('Model selection decision:', {
    shouldUseAdvanced,
    meetsThresholds,
    isCoreCategory,
    isAdvancedCategory,
    selectedModel: shouldUseAdvanced ? 'gpt-4o' : 'gpt-4o-mini'
  });
  
  return shouldUseAdvanced;
};

/**
 * Gets recommended latency tolerance based on session context
 */
export const getRecommendedLatencyTolerance = (
  sessionType: string,
  categoryId?: string,
  hasBiography?: boolean
): number => {
  // Sample sessions: prioritize speed
  if (sessionType === 'sample') {
    return 3;
  }
  
  // Biography-heavy sessions: allow quality time
  if (hasBiography) {
    return 8;
  }
  
  // ZEN-AF/News Meditation: allow quality time for nuanced language handling
  if (categoryId === 'zen-af' || 
      categoryId === 'news-meditation' ||
      sessionType === 'explicit') {
    return 10;
  }
  
  // Standard categories: balanced approach
  return 5;
};